"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@/lib/store";
import { useRouter } from "next/navigation";
import { getSettings, type Settings } from "@/lib/settings-api";
import ProfileSection from "@/components/settings/ProfileSection";
import ToneSection from "@/components/settings/ToneSection";
import AIMemorySection from "@/components/settings/AIMemorySection";
import ThemeSection from "@/components/settings/ThemeSection";
import AutonomySection from "@/components/settings/AutonomySection";
import QuietHoursSection from "@/components/settings/QuietHoursSection";
import WorkingHoursSection from "@/components/settings/WorkingHoursSection";
import EnergySection from "@/components/settings/EnergySection";
import GoogleSection from "@/components/settings/GoogleSection";
import PushSection from "@/components/settings/PushSection";
import NotionSection from "@/components/settings/NotionSection";

export default function SettingsScreen() {
  const { user, logout } = useAuth();
  const router = useRouter();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      getSettings()
        .then(setSettings)
        .catch((e) => setError(e.message));
    }
  }, [user]);

  const handleSignOut = async () => {
    await logout();
    router.replace("/sign-in");
  };

  return (
    <div className="px-4 py-6 pb-24 max-w-lg mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-text">Settings</h1>
        <p className="text-sm text-muted mt-0.5">Customise your experience</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-amber/10 border border-amber/30 rounded-xl text-sm text-amber">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <ProfileSection settings={settings} onUpdate={setSettings} />
        <ToneSection settings={settings} onUpdate={setSettings} />
        <AIMemorySection />
        <ThemeSection settings={settings} onUpdate={setSettings} />
        <AutonomySection settings={settings} onUpdate={setSettings} />
        <WorkingHoursSection settings={settings} onUpdate={setSettings} />
        <EnergySection settings={settings} onUpdate={setSettings} />
        <QuietHoursSection settings={settings} onUpdate={setSettings} />
        <GoogleSection settings={settings} onUpdate={setSettings} />
        <PushSection />
        <NotionSection />

        <div className="pt-4">
          <button
            onClick={handleSignOut}
            data-testid="settings-sign-out"
            className="w-full py-3 bg-rose/10 text-rose rounded-xl font-medium text-sm
                       hover:bg-rose/15 transition-colors border border-rose/30"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
